import React from 'react';
import Box from '@cloudscape-design/components/box';
import HierarchyFilters from './HierarchyFilters';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useTranslate } from 'src/i18n/useTranslate';
import { usePreferences } from './providers/PreferencesProvider';
import { Hotspot } from '@cloudscape-design/components';
import { HOT_SPOTS } from 'src/tutorials/enums';
import { useFavorites } from './providers/FavoritesProvider';
import { ArtifactsSection } from './ArtifactsSection';
import { PAGE } from 'src/pages/enums';
import { useRecentlyViewed } from './providers/RecentlyViewedProvider';
import { PersonalUpdatesBanner } from './PersonalUpdatesBanner';

interface ContentProps {
    modalVersion?: boolean;
    setExploreModalOpen?: (isOpen: boolean) => void;
}

const Content = ({
    modalVersion = false,
    setExploreModalOpen,
}: ContentProps) => {
    const t = useTranslate();

    const { refinementToggleOn } = usePreferences();
    const { favorites } = useFavorites();
    const { recentlyViewed } = useRecentlyViewed();

    return (
        <div
            className={!modalVersion ? 'main-content' : 'explore-modal-content'}
        >
            <SpaceBetween size="m">
                {/* Only on the home page; Content is reused inside the explore
                    modal (`modalVersion=true`), where the personal updates
                    banner has nothing to do and would clutter the modal. */}
                {!modalVersion && <PersonalUpdatesBanner />}
                <div className="flex-row justify-space-between">
                    <Hotspot hotspotId={HOT_SPOTS.GRU_CONTENT_BROWSE}>
                        <Box variant="h2" data-testid="header-text">
                            {t(
                                'home_expandable_heading',
                                'Explore GRU content.'
                            )}
                        </Box>
                    </Hotspot>
                </div>
                <HierarchyFilters
                    modalVersion={modalVersion}
                    setExploreModalOpen={setExploreModalOpen}
                    filterToggleChecked={refinementToggleOn}
                />
                {!modalVersion && (
                    <>
                        <ArtifactsSection
                            artifacts={recentlyViewed
                                .filter((recent) => recent.cardDetails)
                                .filter(
                                    // de-dupe
                                    (recent, index, self) =>
                                        index ===
                                        self.findIndex(
                                            (r) =>
                                                r.id === recent.id &&
                                                r.topicId === recent.topicId
                                        )
                                )
                                .map((recent) => ({
                                    ...recent.cardDetails!,
                                    id: recent.id,
                                    title: recent.topicName
                                        ? `${recent.cardDetails!.title} > ${
                                              recent.topicName
                                          }`
                                        : recent.cardDetails!.title,
                                    topicId: recent.topicId,
                                    topicName: recent.topicName,
                                }))}
                            name={t(
                                'landing_page_recently_view',
                                'Recently Viewed'
                            )}
                            viewAllPath={`/recent`}
                        />

                        <ArtifactsSection
                            artifacts={favorites
                                .filter((fav) => fav.cardDetails)
                                .map((fav) => ({
                                    ...fav.cardDetails!,
                                    id: fav.id,
                                    title: fav.topicName
                                        ? `${fav.cardDetails!.title} > ${
                                              fav.topicName
                                          }`
                                        : fav.cardDetails!.title,
                                    topicId: fav.topicId,
                                    topicName: fav.topicName,
                                }))}
                            name={t(
                                'landing_page_recent_favorites',
                                'Recent Favorites'
                            )}
                            viewAllPath={`/${PAGE.FAVORITES}`}
                        />
                    </>
                )}
            </SpaceBetween>
        </div>
    );
};

export default Content;
